import { Injectable, inject } from '@angular/core';

import { WidgetConfig } from '@frontend/sports/common/client-config-data-access';
import { ModuleLoaderService } from '@frontend/sports/deferred/feature/module-loader';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { AdaptiveLayoutService } from '@frontend/sports/layout/feature';
import { Widget, WidgetLayoutTemplate, WidgetPage } from '@frontend/sports/types/components/widget';
import { Observable, ReplaySubject, distinctUntilKeyChanged, filter } from 'rxjs';

import { widgetsToWidgetTypes } from './widgets-to-widget-types.util';

export interface WidgetRightColumnContent {
    widgets: Widget<unknown>[];
    page?: WidgetPage;
    layoutTemplate?: WidgetLayoutTemplate;
}

@Injectable({
    providedIn: 'root',
})
export class WidgetRightColumnService {
    private config = inject(WidgetConfig);
    private moduleLoader = inject(ModuleLoaderService);

    private widgets = new ReplaySubject<WidgetRightColumnContent>(1);

    constructor() {
        const layout = inject(AdaptiveLayoutService);
        const productUtils = inject(SportsProductUtils);

        layout.stateChange$
            .pipe(
                distinctUntilKeyChanged('hasRightColumn'),
                productUtils.filterSportsEmitLast(),
                filter((current) => current.hasRightColumn),
            )
            .subscribe(() => this.resetContent());
        this.widgets.pipe(filter(({ widgets }) => widgets.length > 0)).subscribe(({ widgets }) => {
            const widgetTypes = widgetsToWidgetTypes(widgets);
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.moduleLoader.lazyLoadFeatures(widgetTypes!);
        });
        this.setContent([]);
    }

    setContent(widgets: Widget<unknown>[], page?: WidgetPage, layoutTemplate?: WidgetLayoutTemplate): void {
        if (widgets.length === 0) {
            // eslint-disable-next-line no-param-reassign
            widgets = this.config.defaultRightColumn;
        }

        this.widgets.next({
            widgets,
            page,
            layoutTemplate,
        });
    }

    resetContent(): void {
        this.setContent([]);
    }

    asObservable(): Observable<WidgetRightColumnContent> {
        return this.widgets.asObservable();
    }
}
